import React from 'react';
import SiteLayout from '../components/SiteLayout';
import { Check, Laptop, Users, FileText, Bell, BarChart3, Cloud, DollarSign, ClipboardCheck, Award, Zap, Loader2, AlertCircle, Brain, Shield, Calendar, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useCMSContent } from '../hooks/useCMSContent';

// Icon mapping for dynamic icon rendering from CMS
const iconMap = {
  Laptop, Users, FileText, Bell, BarChart3, Cloud, DollarSign, ClipboardCheck, Award, Zap, Brain, Shield, Calendar, Settings
};

const FeaturesPage = () => {
  const { content: cmsContent, loading } = useCMSContent('features-page');

  if (loading) {
    return (
      <SiteLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SiteLayout>
    );
  }

  // Use CMS content only - no fallbacks
  const hero = cmsContent?.hero;
  const modules = cmsContent?.modules;
  const cta = cmsContent?.cta;

  // Show error if required CMS content is not configured
  if (!hero || !modules) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">CMS Content Required</h2>
            <p className="text-muted-foreground mb-4">
              Features page content is not configured. Please add content to the CMS document: <code className="bg-muted px-1 rounded">cms_content/features-page</code>
            </p>
            <p className="text-sm text-muted-foreground">
              Required fields: hero, modules
            </p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {hero && (
        <section className="py-20 bg-muted/30">
          <div className="container-wide text-center">
            {hero.title && (
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {hero.title}
              </h1>
            )}
            {hero.subtitle && (
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {hero.subtitle}
              </p>
            )}
            {hero.heroImage && (
              <div className="mt-12 relative mx-auto max-w-5xl rounded-xl overflow-hidden shadow-2xl border border-border/50">
                <img
                  src={hero.heroImage}
                  alt={hero.heroImageAlt || ''}
                  className="w-full h-auto object-cover"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {modules && modules.length > 0 && (
        <section className="py-20">
          <div className="container-wide">
            <div className="grid md:grid-cols-2 gap-12">
              {modules.map((module, index) => {
                const Icon = iconMap[module.icon] || ClipboardCheck;
                return (
                  <div key={module.id || index} className="bg-card border border-border rounded-xl p-8 hover:shadow-md transition-all">
                    {module.image && (
                      <div className="mb-6 rounded-lg overflow-hidden border border-border/50">
                        <img src={module.image} alt={module.title} className="w-full h-48 object-cover" />
                      </div>
                    )}
                    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4">{module.title}</h3>
                    <p className="text-muted-foreground mb-8 text-lg">
                      {module.description}
                    </p>
                    {module.features && module.features.length > 0 && (
                      <ul className="space-y-3">
                        {module.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                              <Check size={14} className="text-green-600" />
                            </div>
                            <div>
                              <span className="font-medium">{feature.name}</span>
                              {feature.description && (
                                <p className="text-sm text-muted-foreground">{feature.description}</p>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {cta && (
        <section className="py-20 bg-primary text-white text-center">
          <div className="container-wide">
            {cta.title && <h2 className="text-3xl font-bold mb-6">{cta.title}</h2>}
            <div className="flex gap-4 justify-center">
              {cta.primaryButton && cta.primaryHref && (
                <Link to={cta.primaryHref}>
                  <Button size="xl" className="bg-white text-primary hover:bg-white/90">
                    {cta.primaryButton}
                  </Button>
                </Link>
              )}
              {cta.secondaryButton && cta.secondaryHref && (
                <Link to={cta.secondaryHref}>
                  <Button size="xl" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                    {cta.secondaryButton}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </section>
      )}
    </SiteLayout>
  );
};

export default FeaturesPage;
